'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function VaMortgageCalculator() {
  const t = useTranslations("tools.vaMortgageCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const [inputs, setInputs] = useState({
    homePrice: 500000,
    downPayment: 0,
    downPaymentUnit: 'percent',
    loanTerm: 30,
    interestRate: 6.286,
    eligibility: 'veteran',
    usedBefore: 'no',
    disabled: 'no',
    fundingFee: 'financed',
    propertyTax: 1.2,
    propertyTaxUnit: 'percent',
    homeInsurance: 2500,
    hoaFee: 0,
    otherCosts: 0,
  });

  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [results, setResults] = useState<any>(null);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const getFundingFeeRate = () => {
    const { eligibility, usedBefore, disabled } = inputs;
    const downPaymentPercent = getDownPaymentPercent();

    if (disabled === 'yes') {
      return 0;
    }

    if (eligibility === 'veteran' || eligibility === 'reservist') {
      if (usedBefore === 'no') {
        if (downPaymentPercent === 0) return 2.15;
        if (downPaymentPercent >= 5 && downPaymentPercent < 10) return 1.5;
        if (downPaymentPercent >= 10) return 1.25;
      } else {
        if (downPaymentPercent === 0) return 3.3;
        if (downPaymentPercent >= 5 && downPaymentPercent < 10) return 1.5;
        if (downPaymentPercent >= 10) return 1.25;
      }
    }

    return 2.15;
  };

  const getDownPaymentPercent = () => {
    const { homePrice, downPayment, downPaymentUnit } = inputs;

    if (downPaymentUnit === 'percent') {
      return downPayment;
    } else {
      return homePrice > 0 ? (downPayment / homePrice) * 100 : 0;
    }
  };

  const formatCurrency = (value: number) => {
    return '$' + value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const { homePrice, downPayment, downPaymentUnit, loanTerm, interestRate, fundingFee, propertyTax, propertyTaxUnit, homeInsurance, hoaFee, otherCosts } = inputs;

    let downPaymentAmount = 0;
    if (downPaymentUnit === 'percent') {
      downPaymentAmount = homePrice * (downPayment / 100);
    } else {
      downPaymentAmount = downPayment;
    }

    const fundingFeeRate = getFundingFeeRate();
    const loanBeforeFee = homePrice - downPaymentAmount;
    const fundingFeeAmount = loanBeforeFee * (fundingFeeRate / 100);

    let loanAmount = loanBeforeFee;
    if (fundingFee === 'financed') {
      loanAmount = loanBeforeFee + fundingFeeAmount;
    }

    const monthlyRate = interestRate / 100 / 12;
    const numPayments = loanTerm * 12;

    let monthlyPrincipalInterest = 0;
    if (monthlyRate > 0) {
      monthlyPrincipalInterest = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
    } else {
      monthlyPrincipalInterest = loanAmount / numPayments;
    }

    let monthlyPropertyTax = 0;
    if (propertyTaxUnit === 'percent') {
      monthlyPropertyTax = (homePrice * (propertyTax / 100)) / 12;
    } else {
      monthlyPropertyTax = propertyTax / 12;
    }

    const monthlyInsurance = homeInsurance / 12;
    const monthlyHOA = hoaFee / 12;
    const monthlyOther = otherCosts / 12;

    const totalMonthlyPayment = monthlyPrincipalInterest + monthlyPropertyTax + monthlyInsurance + monthlyHOA + monthlyOther;

    const totalPayments = monthlyPrincipalInterest * numPayments;
    const totalInterest = totalPayments - loanAmount;

    const principalPercent = (loanAmount / (loanAmount + totalInterest)) * 100;
    const interestPercent = (totalInterest / (loanAmount + totalInterest)) * 100;

    setResults({
      monthlyPayment: totalMonthlyPayment,
      homePrice,
      fundingFee: fundingFeeAmount,
      fundingFeeRate,
      downPayment: downPaymentAmount,
      loanAmount,
      totalPayments,
      totalInterest,
      principalPercent,
      interestPercent,
    });
  };

  const createPieChart = () => {
    if (!results) return null;

    const { loanAmount, totalInterest } = results;
    const total = loanAmount + totalInterest;
    const principalAngle = (loanAmount / total) * 360;

    const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
      const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
      return {
        x: centerX + (radius * Math.cos(angleInRadians)),
        y: centerY + (radius * Math.sin(angleInRadians))
      };
    };

    const describeArc = (x: number, y: number, radius: number, startAngle: number, endAngle: number) => {
      const start = polarToCartesian(x, y, radius, endAngle);
      const end = polarToCartesian(x, y, radius, startAngle);
      const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
      return [
        "M", x, y,
        "L", start.x, start.y,
        "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
        "Z"
      ].join(" ");
    };

    const cx = 100;
    const cy = 100;
    const radius = 80;

    return (
      <svg width="200" height="200" viewBox="0 0 200 200" style={{ margin: '1rem auto', display: 'block' }}>
        <path d={describeArc(cx, cy, radius, 0, principalAngle)} fill="#2563eb" />
        <path d={describeArc(cx, cy, radius, principalAngle, 360)} fill="#8bbc21" />
        <circle cx={cx} cy={cy} r="30" fill="white" />
      </svg>
    );
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '2rem' },
    { padding: '3rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '3rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '1000px' },
    { maxWidth: '1200px' }
  );

  const calculatorLayoutStyle = getResponsiveStyle(
    { display: 'grid', gap: '2rem', alignItems: 'start' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const formCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    {},
    { marginBottom: '1.25rem' },
    { marginBottom: '1.5rem' },
    { marginBottom: '1.5rem' }
  );

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', color: '#334155', fontWeight: 600 },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputWrapperStyle = {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const inputStyle = getResponsiveStyle(
    {
      flex: 1,
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      outline: 'none',
    },
    { padding: '0.75rem 1rem', fontSize: '1rem' },
    { padding: '0.875rem 1.25rem', fontSize: '1.125rem' },
    { padding: '1rem 1.5rem', fontSize: '1.125rem' }
  );

  const selectStyle = getResponsiveStyle(
    {
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      background: 'white',
      cursor: 'pointer',
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1.125rem' },
    { padding: '1rem', fontSize: '1.125rem' }
  );

  const radioGroupStyle = getResponsiveStyle(
    {
      marginTop: '1.5rem',
      background: '#f8fafc',
      borderRadius: '0.5rem',
    },
    { padding: '1rem' },
    { padding: '1rem' },
    { padding: '1rem' }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      marginTop: '1rem',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1.25rem 2.5rem', fontSize: '1.25rem' }
  );

  const resultsCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem', position: 'sticky' as const, top: '2rem' }
  );

  const monthlyPaymentBoxStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      borderRadius: '0.75rem',
      textAlign: 'center' as const,
      marginBottom: '1.5rem',
    },
    { padding: '1.25rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <h2 style={{ ...h2Style, textAlign: 'center', marginBottom: '2rem' }}>{t("toolTitle")}</h2>

          <div style={calculatorLayoutStyle}>
            <div style={formCardStyle}>
              <form onSubmit={handleCalculate}>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="homePrice">{t("homePrice")}</label>
                  <div style={inputWrapperStyle}>
                    <input
                      style={inputStyle}
                      type="number"
                      id="homePrice"
                      value={inputs.homePrice}
                      onChange={(e) => setInputs({...inputs, homePrice: parseFloat(e.target.value) || 0})}
                      min="0"
                      step="1000"
                      required
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="downPayment">{t("downPayment")}</label>
                  <div style={inputWrapperStyle}>
                    <input
                      style={inputStyle}
                      type="number"
                      id="downPayment"
                      value={inputs.downPayment}
                      onChange={(e) => setInputs({...inputs, downPayment: parseFloat(e.target.value) || 0})}
                      min="0"
                      step="1000"
                    />
                    <select
                      style={selectStyle}
                      value={inputs.downPaymentUnit}
                      onChange={(e) => setInputs({...inputs, downPaymentUnit: e.target.value})}
                    >
                      <option value="percent">%</option>
                      <option value="dollar">$</option>
                    </select>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="loanTerm">{t("loanTerm")}</label>
                  <div style={inputWrapperStyle}>
                    <input
                      style={inputStyle}
                      type="number"
                      id="loanTerm"
                      value={inputs.loanTerm}
                      onChange={(e) => setInputs({...inputs, loanTerm: parseFloat(e.target.value) || 30})}
                      min="1"
                      max="30"
                      required
                    />
                    <span style={{ color: '#64748b', fontWeight: 500 }}>{t("years")}</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="interestRate">{t("interestRate")}</label>
                  <div style={inputWrapperStyle}>
                    <input
                      style={inputStyle}
                      type="number"
                      id="interestRate"
                      value={inputs.interestRate}
                      onChange={(e) => setInputs({...inputs, interestRate: parseFloat(e.target.value) || 0})}
                      min="0"
                      max="20"
                      step="0.001"
                      required
                    />
                    <span style={{ color: '#64748b', fontWeight: 500 }}>%</span>
                  </div>
                </div>

                <div style={radioGroupStyle}>
                  <div style={{ fontWeight: 600, color: '#334155', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
                    {t("vaEligibility")}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="radio"
                        id="eligibility1"
                        name="eligibility"
                        value="veteran"
                        checked={inputs.eligibility === 'veteran'}
                        onChange={(e) => setInputs({...inputs, eligibility: e.target.value})}
                        style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer' }}
                      />
                      <label htmlFor="eligibility1" style={{ margin: 0, cursor: 'pointer', fontWeight: 400 }}>
                        {t("activeVeteran")}
                      </label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="radio"
                        id="eligibility2"
                        name="eligibility"
                        value="reservist"
                        checked={inputs.eligibility === 'reservist'}
                        onChange={(e) => setInputs({...inputs, eligibility: e.target.value})}
                        style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer' }}
                      />
                      <label htmlFor="eligibility2" style={{ margin: 0, cursor: 'pointer', fontWeight: 400 }}>
                        {t("reservistGuard")}
                      </label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="radio"
                        id="eligibility3"
                        name="eligibility"
                        value="spouse"
                        checked={inputs.eligibility === 'spouse'}
                        onChange={(e) => setInputs({...inputs, eligibility: e.target.value})}
                        style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer' }}
                      />
                      <label htmlFor="eligibility3" style={{ margin: 0, cursor: 'pointer', fontWeight: 400 }}>
                        {t("survivingSpouse")}
                      </label>
                    </div>
                  </div>
                </div>

                <div style={radioGroupStyle}>
                  <div style={{ fontWeight: 600, color: '#334155', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
                    {t("usedVaBefore")}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="radio"
                        id="usedBefore1"
                        name="usedBefore"
                        value="yes"
                        checked={inputs.usedBefore === 'yes'}
                        onChange={(e) => setInputs({...inputs, usedBefore: e.target.value})}
                        style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer' }}
                      />
                      <label htmlFor="usedBefore1" style={{ margin: 0, cursor: 'pointer', fontWeight: 400 }}>
                        {t("yes")}
                      </label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="radio"
                        id="usedBefore2"
                        name="usedBefore"
                        value="no"
                        checked={inputs.usedBefore === 'no'}
                        onChange={(e) => setInputs({...inputs, usedBefore: e.target.value})}
                        style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer' }}
                      />
                      <label htmlFor="usedBefore2" style={{ margin: 0, cursor: 'pointer', fontWeight: 400 }}>
                        {t("no")}
                      </label>
                    </div>
                  </div>
                </div>

                <div style={radioGroupStyle}>
                  <div style={{ fontWeight: 600, color: '#334155', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
                    {t("disabilityRating")}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="radio"
                        id="disabled1"
                        name="disabled"
                        value="yes"
                        checked={inputs.disabled === 'yes'}
                        onChange={(e) => setInputs({...inputs, disabled: e.target.value})}
                        style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer' }}
                      />
                      <label htmlFor="disabled1" style={{ margin: 0, cursor: 'pointer', fontWeight: 400 }}>
                        {t("yes")}
                      </label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="radio"
                        id="disabled2"
                        name="disabled"
                        value="no"
                        checked={inputs.disabled === 'no'}
                        onChange={(e) => setInputs({...inputs, disabled: e.target.value})}
                        style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer' }}
                      />
                      <label htmlFor="disabled2" style={{ margin: 0, cursor: 'pointer', fontWeight: 400 }}>
                        {t("no")}
                      </label>
                    </div>
                  </div>
                </div>

                <div style={radioGroupStyle}>
                  <div style={{ fontWeight: 600, color: '#334155', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
                    {t("fundingFeeOption")}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="radio"
                        id="fundingFee1"
                        name="fundingFee"
                        value="financed"
                        checked={inputs.fundingFee === 'financed'}
                        onChange={(e) => setInputs({...inputs, fundingFee: e.target.value})}
                        style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer' }}
                      />
                      <label htmlFor="fundingFee1" style={{ margin: 0, cursor: 'pointer', fontWeight: 400 }}>
                        {t("financedIntoLoan")}
                      </label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="radio"
                        id="fundingFee2"
                        name="fundingFee"
                        value="upfront"
                        checked={inputs.fundingFee === 'upfront'}
                        onChange={(e) => setInputs({...inputs, fundingFee: e.target.value})}
                        style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer' }}
                      />
                      <label htmlFor="fundingFee2" style={{ margin: 0, cursor: 'pointer', fontWeight: 400 }}>
                        {t("paidUpfront")}
                      </label>
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: 'center', margin: '1.5rem 0' }}>
                  <button
                    type="button"
                    onClick={() => setShowMoreOptions(!showMoreOptions)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#2563eb',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: '1rem',
                      padding: '0.5rem 1rem',
                    }}
                  >
                    {showMoreOptions ? t("lessOptions") : t("moreOptions")}
                  </button>
                </div>

                {showMoreOptions && (
                  <div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="propertyTax">{t("propertyTax")}</label>
                      <div style={inputWrapperStyle}>
                        <input
                          style={inputStyle}
                          type="number"
                          id="propertyTax"
                          value={inputs.propertyTax}
                          onChange={(e) => setInputs({...inputs, propertyTax: parseFloat(e.target.value) || 0})}
                          min="0"
                          step="0.1"
                        />
                        <select
                          style={selectStyle}
                          value={inputs.propertyTaxUnit}
                          onChange={(e) => setInputs({...inputs, propertyTaxUnit: e.target.value})}
                        >
                          <option value="percent">%</option>
                          <option value="dollar">$</option>
                        </select>
                      </div>
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="homeInsurance">{t("homeInsurance")}</label>
                      <div style={inputWrapperStyle}>
                        <input
                          style={inputStyle}
                          type="number"
                          id="homeInsurance"
                          value={inputs.homeInsurance}
                          onChange={(e) => setInputs({...inputs, homeInsurance: parseFloat(e.target.value) || 0})}
                          min="0"
                          step="100"
                        />
                        <span style={{ color: '#64748b', fontWeight: 500 }}>{t("perYear")}</span>
                      </div>
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="hoaFee">{t("hoaFee")}</label>
                      <div style={inputWrapperStyle}>
                        <input
                          style={inputStyle}
                          type="number"
                          id="hoaFee"
                          value={inputs.hoaFee}
                          onChange={(e) => setInputs({...inputs, hoaFee: parseFloat(e.target.value) || 0})}
                          min="0"
                          step="50"
                        />
                        <span style={{ color: '#64748b', fontWeight: 500 }}>{t("perYear")}</span>
                      </div>
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="otherCosts">{t("otherCosts")}</label>
                      <div style={inputWrapperStyle}>
                        <input
                          style={inputStyle}
                          type="number"
                          id="otherCosts"
                          value={inputs.otherCosts}
                          onChange={(e) => setInputs({...inputs, otherCosts: parseFloat(e.target.value) || 0})}
                          min="0"
                          step="100"
                        />
                        <span style={{ color: '#64748b', fontWeight: 500 }}>{t("perYear")}</span>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  style={calculateButtonStyle}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 10px 20px rgba(37, 99, 235, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {t("calculateButton")}
                </button>
              </form>
            </div>

            {results && (
              <div style={resultsCardStyle}>
                <h3 style={{ fontSize: '1.5rem', color: colors.textPrimary, marginBottom: '1.5rem', textAlign: 'center' }}>
                  {t("results")}
                </h3>

                <div style={monthlyPaymentBoxStyle}>
                  <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '0.5rem' }}>
                    {t("monthlyPayment")}
                  </div>
                  <div style={{ fontSize: screenSize === 'mobile' ? '2rem' : '2.5rem', fontWeight: 700 }}>
                    {formatCurrency(results.monthlyPayment)}
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem 0', borderBottom: '1px solid #e2e8f0' }}>
                  <span style={{ color: '#64748b', fontWeight: 500 }}>{t("homePriceLabel")}</span>
                  <span style={{ color: colors.textPrimary, fontWeight: 700 }}>{formatCurrency(results.homePrice)}</span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem 0', borderBottom: '1px solid #e2e8f0' }}>
                  <span style={{ color: '#64748b', fontWeight: 500 }}>{t("fundingFeeLabel")}</span>
                  <span style={{ color: colors.textPrimary, fontWeight: 700 }}>
                    {formatCurrency(results.fundingFee)} ({results.fundingFeeRate.toFixed(2)}%)
                  </span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem 0', borderBottom: '1px solid #e2e8f0' }}>
                  <span style={{ color: '#64748b', fontWeight: 500 }}>{t("downPaymentLabel")}</span>
                  <span style={{ color: colors.textPrimary, fontWeight: 700 }}>{formatCurrency(results.downPayment)}</span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem 0', borderBottom: '1px solid #e2e8f0' }}>
                  <span style={{ color: '#64748b', fontWeight: 500 }}>{t("loanAmountLabel")}</span>
                  <span style={{ color: colors.textPrimary, fontWeight: 700 }}>{formatCurrency(results.loanAmount)}</span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem 0', borderBottom: '1px solid #e2e8f0' }}>
                  <span style={{ color: '#64748b', fontWeight: 500 }}>{t("totalPaymentsLabel")}</span>
                  <span style={{ color: colors.textPrimary, fontWeight: 700 }}>{formatCurrency(results.totalPayments)}</span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem 0' }}>
                  <span style={{ color: '#64748b', fontWeight: 500 }}>{t("totalInterestLabel")}</span>
                  <span style={{ color: colors.textPrimary, fontWeight: 700 }}>{formatCurrency(results.totalInterest)}</span>
                </div>

                <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                  {createPieChart()}
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginTop: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ width: '1rem', height: '1rem', borderRadius: '0.25rem', background: '#2563eb' }}></div>
                      <span>{t("principal")} ({results.principalPercent.toFixed(0)}%)</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ width: '1rem', height: '1rem', borderRadius: '0.25rem', background: '#8bbc21' }}></div>
                      <span>{t("interest")} ({results.interestPercent.toFixed(0)}%)</span>
                    </div>
                  </div>
                </div>

                <div style={{
                  background: '#fef3c7',
                  borderLeft: '4px solid #f59e0b',
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  marginTop: '1.5rem',
                  fontSize: '0.9rem',
                  color: '#92400e'
                }}>
                  💡 <strong>{t("tipLabel")}</strong> {t("tipText")}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introPara1")}</p>
        <p style={paragraphStyle}>{t("introPara2")}</p>
        <p style={paragraphStyle}>{t("introPara3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={{ paddingLeft: '1.5rem', color: colors.textSecondary }}>
          <li style={paragraphStyle}>{t("step1")}</li>
          <li style={paragraphStyle}>{t("step2")}</li>
          <li style={paragraphStyle}>{t("step3")}</li>
          <li style={paragraphStyle}>{t("step4")}</li>
          <li style={paragraphStyle}>{t("step5")}</li>
          <li style={paragraphStyle}>{t("step6")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <ul style={{ paddingLeft: '1.5rem', color: colors.textSecondary }}>
          <li style={paragraphStyle}>{t("insight1")}</li>
          <li style={paragraphStyle}>{t("insight2")}</li>
          <li style={paragraphStyle}>{t("insight3")}</li>
          <li style={paragraphStyle}>{t("insight4")}</li>
          <li style={paragraphStyle}>{t("insight5")}</li>
          <li style={paragraphStyle}>{t("insight6")}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>
        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t("fundingFeeTitle")}
        </h3>
        <p style={paragraphStyle}>{t("fundingFeePara")}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t("benefitsTitle")}
        </h3>
        <ul style={{ paddingLeft: '1.5rem', color: colors.textSecondary }}>
          <li style={paragraphStyle}>{t("benefit1")}</li>
          <li style={paragraphStyle}>{t("benefit2")}</li>
          <li style={paragraphStyle}>{t("benefit3")}</li>
          <li style={paragraphStyle}>{t("benefit4")}</li>
          <li style={paragraphStyle}>{t("benefit5")}</li>
        </ul>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t("considerationsTitle")}
        </h3>
        <ul style={{ paddingLeft: '1.5rem', color: colors.textSecondary }}>
          <li style={paragraphStyle}>{t("consideration1")}</li>
          <li style={paragraphStyle}>{t("consideration2")}</li>
          <li style={paragraphStyle}>{t("consideration3")}</li>
          <li style={paragraphStyle}>{t("consideration4")}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem' }}>
            {t("faq1Question")}
          </h3>
          <p style={paragraphStyle}>{t("faq1Answer")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem' }}>
            {t("faq2Question")}
          </h3>
          <p style={paragraphStyle}>{t("faq2Answer")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem' }}>
            {t("faq3Question")}
          </h3>
          <p style={paragraphStyle}>{t("faq3Answer")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem' }}>
            {t("faq4Question")}
          </h3>
          <p style={paragraphStyle}>{t("faq4Answer")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem' }}>
            {t("faq5Question")}
          </h3>
          <p style={paragraphStyle}>{t("faq5Answer")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem' }}>
            {t("faq6Question")}
          </h3>
          <p style={paragraphStyle}>{t("faq6Answer")}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ul style={{ paddingLeft: '1.5rem', color: colors.textSecondary }}>
          <li style={paragraphStyle}>
            <a href="https://www.creditcaptain.com/tools/va-mortgage-calculator" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary }}>
              {t("ref1")}
            </a>
          </li>
          <li style={paragraphStyle}>
            <a href="https://www.veteransunited.com/education/tools/mortgage-calculator/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary }}>
              {t("ref2")}
            </a>
          </li>
          <li style={paragraphStyle}>
            <a href="https://www.calculator.net/va-mortgage-calculator.html" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary }}>
              {t("ref3")}
            </a>
          </li>
          <li style={paragraphStyle}>
            <a href="https://www.navyfederal.org/makingcents/tools/va-loan-calculator.html" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary }}>
              {t("ref4")}
            </a>
          </li>
          <li style={paragraphStyle}>
            <a href="https://www.benefits.va.gov/homeloans/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary }}>
              {t("ref5")}
            </a>
          </li>
          <li style={paragraphStyle}>
            <a href="https://valoannetwork.com/how-much-va-loan-can-i-afford/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary }}>
              {t("ref6")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}